import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Cloud, ArrowRight, Check, AlertCircle,
  Shield, Layout, DollarSign, Server,
  FileSearch, Gauge
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

export default function CloudEngineeringPage() {
  const problems = [
    'Unstructured cloud sprawl and rising costs.',
    'Legacy systems not ready for the cloud.',
    'Security and compliance concerns.',
    'Lack of standardized environments across teams.'
  ];

  const outcomes = [
    { icon: Layout, title: 'Well-architected foundation', description: 'Secure, scalable and compliant cloud infrastructure.' },
    { icon: Server, title: 'Standardized environments', description: 'Consistent dev, test and prod environments.' },
    { icon: DollarSign, title: 'Clear cost visibility', description: 'Understand and optimize cloud spending.' },
    { icon: Shield, title: 'Improved security', description: 'Enhanced security and resilience across workloads.' }
  ];

  const scope = [
    'Cloud landing zone design (Azure-first)',
    'Migration and modernization of workloads',
    'Infrastructure as Code (IaC)',
    'CI/CD pipelines and DevOps practices',
    'Monitoring, logging and incident readiness'
  ];

  const engagements = [
    { icon: FileSearch, title: 'Cloud assessment', duration: '4–6 weeks', description: 'Evaluate current state and plan migration path.' },
    { icon: Cloud, title: 'Migration project', duration: '3–9 months', description: 'Execute migration with minimal disruption.' },
    { icon: Gauge, title: 'Platform engineering', duration: 'Ongoing', description: 'Continuous improvement and optimization.' }
  ];

  const useCases = [
    {
      title: 'Azure Landing Zone for Fintech',
      problem: 'Cloud infrastructure grew organically without governance, leading to security gaps.',
      solution: 'Designed and implemented Azure landing zone with proper network segmentation.',
      result: 'Achieved compliance requirements, reduced cloud costs by 30%.'
    },
    {
      title: 'Legacy to Cloud Migration',
      problem: 'Enterprise running critical workloads on aging on-premise infrastructure.',
      solution: 'Planned and executed phased migration to Azure, modernizing applications.',
      result: 'Reduced infrastructure costs by 40%, improved application performance.'
    }
  ];

  return (
    <div data-testid="cloud-engineering-page">
      <Helmet>
        <title>Cloud Engineering - Cynergy Services</title>
        <meta name="description" content="Secure, scalable and cost-efficient cloud foundations, with an Azure-first approach. Cloud engineering services by Cynergy." />
      </Helmet>

      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 via-white to-accent/5" data-testid="cloud-hero">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <motion.div initial="initial" animate="animate" variants={fadeInUp}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                  <Cloud className="w-5 h-5 text-primary" />
                </div>
                <p className="text-sm font-medium uppercase tracking-widest text-primary">Service</p>
              </div>
              <h1 className="heading-1 text-foreground mb-4">Cloud Engineering</h1>
              <p className="body-lg mb-6">Secure, scalable and cost-efficient cloud foundations, with an Azure-first approach.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/contact">
                  <Button size="lg" className="btn-primary w-full sm:w-auto" data-testid="cloud-cta-primary">
                    Request a cloud architecture review
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="cloud-cta-secondary">
                    Talk to a cloud architect
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_engineering-hub-38/artifacts/ssoiec65_Cloud%20Engineering.jpeg"
                alt="Cloud computing infrastructure"
                className="w-full rounded-sm shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problems & Outcomes Combined */}
      <section className="section-padding bg-white" data-testid="cloud-problems-outcomes">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Challenges</p>
              <h2 className="heading-2 text-foreground mb-6">Problems we solve</h2>
              <ul className="space-y-3">
                {problems.map((problem, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`cloud-problem-${index}`}>
                    <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{problem}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Results</p>
              <h2 className="heading-2 text-foreground mb-6">Outcomes</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {outcomes.map((outcome, index) => (
                  <Card key={outcome.title} className="bg-secondary/30 border-0" data-testid={`cloud-outcome-${index}`}>
                    <CardContent className="p-4">
                      <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center mb-3">
                        <outcome.icon className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="font-semibold text-foreground text-sm mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {outcome.title}
                      </h3>
                      <p className="text-xs text-muted-foreground">{outcome.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Scope & Engagements */}
      <section className="section-padding bg-secondary/50" data-testid="cloud-scope">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Capabilities</p>
              <h2 className="heading-2 text-foreground mb-6">Typical scope</h2>
              <ul className="space-y-3">
                {scope.map((item, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`cloud-scope-${index}`}>
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Engagement Models</p>
              <h2 className="heading-2 text-foreground mb-6">How we work</h2>
              <div className="space-y-3">
                {engagements.map((engagement, index) => (
                  <Card key={engagement.title} className="border-border/60" data-testid={`cloud-engagement-${index}`}>
                    <CardContent className="p-4 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <engagement.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {engagement.title}
                          </h3>
                          <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
                            {engagement.duration}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">{engagement.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-padding bg-white" data-testid="cloud-use-cases">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Case Studies</p>
            <h2 className="heading-2 text-foreground">Example architectures</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full bg-secondary/30 border-0" data-testid={`cloud-use-case-${index}`}>
                  <CardContent className="p-6">
                    <h3 className="heading-3 text-foreground mb-4">{useCase.title}</h3>
                    <div className="space-y-3 text-sm">
                      <p><span className="font-medium text-primary">Problem:</span> <span className="text-muted-foreground">{useCase.problem}</span></p>
                      <p><span className="font-medium text-primary">Solution:</span> <span className="text-muted-foreground">{useCase.solution}</span></p>
                      <p><span className="font-medium text-primary">Result:</span> <span className="text-muted-foreground">{useCase.result}</span></p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary" data-testid="cloud-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="heading-2 text-white mb-4">Need to get your cloud under control?</h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Let's discuss how we can help you build a secure, scalable cloud foundation.
            </p>
            <Link to="/contact">
              <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90" data-testid="cloud-final-cta">
                Book a cloud consultation
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
